import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, ArrowUp, ArrowDown, Search } from "lucide-react";

export function DataTable({
  data = [],
  columns,
  searchKeys,
  searchPlaceholder = "Cerca...",
  initialSort,
  testid,
  emptyIcon: EmptyIcon,
  emptyText = "Nessun dato",
  rowTestId,
  onRowClick,
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(initialSort || null);

  const filtered = useMemo(() => {
    let rows = data;
    if (searchKeys && query.trim()) {
      const q = query.toLowerCase();
      rows = rows.filter((r) =>
        searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q))
      );
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      const getVal = col?.sortValue || ((r) => r[sort.key]);
      rows = [...rows].sort((a, b) => {
        const va = getVal(a);
        const vb = getVal(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        let cmp;
        if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" });
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, query, sort, searchKeys, columns]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  return (
    <div>
      {searchKeys && (
        <div className="relative mb-3 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            data-testid={testid ? `${testid}-search` : "table-search"}
            className="pl-9"
          />
        </div>
      )}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid={testid ? `${testid}-empty` : undefined}>
          {EmptyIcon && <EmptyIcon className="h-10 w-10 text-slate-300" />}
          <p className="font-heading text-lg font-semibold text-slate-700">{emptyText}</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden" data-testid={testid ? `${testid}-cards` : undefined}>
            {filtered.map((row, ri) => (
              <div
                key={row.id ?? ri}
                data-testid={rowTestId ? `${rowTestId(row)}-m` : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`w-full rounded-lg border border-slate-200 p-3 ${onRowClick ? "cursor-pointer active:bg-slate-50" : ""}`}
              >
                <div className="space-y-2">
                  {columns.map((c) =>
                    c.stacked ? (
                      <div key={c.key} className="space-y-1.5 border-t border-slate-100 pt-2">
                        <span className="block text-xs font-medium uppercase tracking-wide text-slate-400">{c.header}</span>
                        <div className={`w-full min-w-0 break-words text-sm ${c.className || ""}`}>{c.cell ? c.cell(row) : row[c.key]}</div>
                      </div>
                    ) : (
                      <div key={c.key} className="flex items-start justify-between gap-3">
                        <span className="shrink-0 pt-0.5 text-xs font-medium uppercase tracking-wide text-slate-400">{c.header}</span>
                        <span className={`min-w-0 break-words text-right text-sm ${c.className || ""}`}>{c.cell ? c.cell(row) : row[c.key]}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="relative hidden overflow-auto rounded-md border border-slate-100 md:block" style={{ maxHeight: "65vh" }}>
          <Table data-testid={testid}>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e2e8f0]">
              <TableRow>
                {columns.map((c) => (
                  <TableHead
                    key={c.key}
                    className={`${c.align === "right" ? "text-right" : ""} ${c.headerClassName || ""} ${c.sortable ? "cursor-pointer select-none" : ""}`}
                    onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                    data-testid={c.sortable && testid ? `${testid}-sort-${c.key}` : undefined}
                  >
                    <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "justify-end" : ""}`}>
                      {c.header}
                      {c.sortable &&
                        (sort?.key === c.key ? (
                          sort.dir === "asc" ? (
                            <ArrowUp className="h-3.5 w-3.5 text-[#002FA7]" />
                          ) : (
                            <ArrowDown className="h-3.5 w-3.5 text-[#002FA7]" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 text-slate-300" />
                        ))}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row, ri) => (
                <TableRow
                  key={row.id ?? ri}
                  data-testid={rowTestId ? rowTestId(row) : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={`hover:bg-slate-50 ${onRowClick ? "cursor-pointer" : ""}`}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className={`${c.align === "right" ? "text-right" : ""} ${c.className || ""}`}>
                      {c.cell ? c.cell(row) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}
    </div>
  );
}
